import { useEffect } from 'react';
import { Box, Container, Link, Typography } from '@mui/material';
import Header from '../components/header';
import Footer from '../components/footer';
import ContactForm from '../components/contact-form';
import { Eyebrow, Lead, MarkerList, Mono, Section } from '../components/section';
import { brand } from '../thema';
import { useTranslation } from 'react-i18next';
import useLanguage from '../useLanguage';

const ContactPage = () => {
    const { t, i18n } = useTranslation();
    const { language } = useLanguage();
    const list = <T,>(key: string) => t(key, { returnObjects: true }) as unknown as T[];

    useEffect(() => {
        i18n.changeLanguage(language);
    }, []);

    return (
        <Box sx={{ color: brand.ink }}>
            <Header dark />
            <Box component="main">

                {/* ページ見出し */}
                <Box
                    component="section"
                    sx={{
                        backgroundColor: brand.dark,
                        color: '#ffffff',
                        backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px), radial-gradient(ellipse 50% 60% at 85% 30%, rgba(69, 182, 175, 0.12), transparent 70%)',
                        backgroundSize: '56px 56px, 56px 56px, 100% 100%',
                        backgroundPosition: 'center',
                    }}
                >
                    <Container maxWidth="lg" sx={{ px: { xs: 3, md: 4 }, pt: { xs: 16, md: 20 }, pb: { xs: 8, md: 10 } }}>
                        <Eyebrow label={t('contactPage.eyebrow')} tone="dark" />
                        <Typography component="h1" sx={{ mt: 2.5, fontSize: { xs: '2rem', md: '3rem' }, fontWeight: 700, letterSpacing: '0.03em' }}>
                            {t('contactPage.title')}
                        </Typography>
                        <Lead tone="dark">{t('contactPage.lead')}</Lead>
                    </Container>
                </Box>

                <Section tone="alt">
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '4fr 8fr' }, gap: { xs: 6, md: 8 }, alignItems: 'start' }}>
                        {/* サイド情報 */}
                        <Box component="aside" sx={{ order: { xs: 2, md: 1 }, position: { md: 'sticky' }, top: { md: 120 } }}>
                            <Typography component="h2" sx={{ fontSize: '1rem', fontWeight: 700 }}>{t('contactPage.flowTitle')}</Typography>
                            <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0, mt: 2.5, borderTop: `1px solid ${brand.ink}` }}>
                                {list<string>('contactPage.flow').map((step, i) => (
                                    <Box component="li" key={step} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2, borderBottom: `1px solid ${brand.line}` }}>
                                        <Mono color={brand.accentText} size="0.8125rem">{String(i + 1).padStart(2, '0')}</Mono>
                                        <Typography component="span" sx={{ fontSize: '0.9375rem', fontWeight: 500 }}>{step}</Typography>
                                        {i >= 1 && i <= 3 && (
                                            <Box component="span" sx={{ fontSize: '0.6875rem', px: 1, py: 0.25, border: `1px solid ${brand.accentText}`, color: brand.accentText }}>
                                                {t('contactPage.free')}
                                            </Box>
                                        )}
                                    </Box>
                                ))}
                            </Box>

                            <Typography component="h2" sx={{ mt: 6, mb: 2.5, fontSize: '1rem', fontWeight: 700 }}>{t('contactPage.topicsTitle')}</Typography>
                            <MarkerList items={list<string>('contactPage.topics')} />

                            <Link
                                href="/#pricing"
                                underline="none"
                                sx={{ mt: 5, display: 'inline-flex', alignItems: 'center', gap: 1, color: brand.ink, fontSize: '0.875rem', fontWeight: 500, borderBottom: `1px solid ${brand.ink}`, pb: 0.5, '&:hover': { color: brand.accentText, borderColor: brand.accentText } }}
                            >
                                {t('contactPage.pricingLink')}
                                <Mono color="inherit">→</Mono>
                            </Link>
                        </Box>

                        {/* フォーム */}
                        <Box sx={{ order: { xs: 1, md: 2 } }}>
                            <ContactForm />
                        </Box>
                    </Box>
                </Section>
            </Box>

            <Footer />
        </Box>
    );
};

export default ContactPage;
